// hydrate.js — fold live backend data into window.MAV BEFORE the screens read it.
//
// The screens are written against window.MAV's mock shape (jobs[], clientBySlug,
// stageDefs, …). Rather than rewrite 20 screens to fetch, we fetch once here and
// overwrite the relevant MAV slices with mapped live data. Field names already
// match the server's /api/data shape (that was the design contract), so the
// mapping is thin.
//
// Real auth uses live-hydrate.js's guarded snapshots and explicit empty/error
// states. The older sample-preserving loader below is explicit demo mode ONLY.
//
// CRASH-PROOFING: MAV.clientBySlug(slug) returns undefined for unknown slugs in
// the mock (footgun — dashboard does clientBySlug(j.client).name). Live builds
// carry client slugs the mock list never heard of (or null), so we (a) synthesize
// a client for any missing slug and (b) wrap clientBySlug to never return null.

import GooseAPI from './api.js';
import { createLiveHydrator } from './live-hydrate.js';

function fmtAgo(ts) {
  if (!ts) return '—';
  const then = new Date(ts).getTime();
  if (isNaN(then)) return '—';
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return Math.max(s, 0) + 's ago';
  const m = Math.floor(s / 60); if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60); if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}

const PALETTE = ['#6334E3', '#0B5C9E', '#1F8A5B', '#C8553D', '#2A6FDB', '#8C6A1E', '#19808A', '#B11116'];

function synthClient(slug) {
  const s = slug || 'unknown';
  const name = s === 'unknown' ? 'Unassigned'
    : s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const color = PALETTE[h % PALETTE.length];
  return {
    slug: s, name, color, accent: color, initials: s.slice(0, 2).toUpperCase(),
    colors: [color, '#1B1B1B', '#8C8C8C'], font: 'Arial', width: 600, refs: 0,
    flags: [], esp: '—', esps: [],
  };
}

function ensureClient(slug) {
  const M = window.MAV;
  const key = slug || 'unknown';
  if (!M.clients.find((c) => c.slug === key)) M.clients.push(synthClient(key));
  return key;
}

// status → how many of the pipeline stages are done (mirrors data.js mkStages intent)
const DONE_BY_STATUS = { queued: 0, running: 3, needs_review: 5, review: 5, complete: 6, done: 6, failed: 3 };
// Figma builds run 5 stages, not 6: run_swarm.py makes the Semantic Parser ("016")
// a no-op for figma ("frames arrive natively"), so the single REST ingest replaces
// ingest + semantic. Drop the semantic stage and shift the done-count down by one for
// any progress past ingest (the removed stage sat at index 1) — so a COMPLETE figma
// build reads "5 of 5", not a phantom "5 of 6" with a Semantic stage that never ran.
const SABRE_STAGE_NAMES = ['Intake', 'Design analysis', 'Sabre decision', 'Deterministic build', 'Deterministic QA', 'Developer approval'];

function synthStages(status, source, activeStage, engine) {
  let defs = window.MAV.stageDefs || [];
  let done = DONE_BY_STATUS[status] ?? 0;
  // For a FAILED build the projector records the REAL death-point index in
  // builds.active_stage (canonical 6-stage order). Use it so a fresh load (before
  // the SSE replay) marks the RIGHT stage failed, instead of the old hardcoded
  // failed→3 guess that rendered e.g. a Semantic crash as "Execution failed".
  // (0 is a valid death point — Ingestion.)
  if (status === 'failed' && Number.isInteger(activeStage) && activeStage >= 0) done = activeStage;
  // Always build the CANONICAL 6-stage rail here — even for figma. The figma
  // collapse (ingest+semantic → one "Figma Ingest", 5 nodes) is DISPLAY-ONLY, done
  // by collapseFigmaIngest in jobdetail.jsx. Dropping the semantic node HERE desynced
  // the rail from the canonical STAGE_SLOT event map (02_planning → slot 2): live
  // SSE events then landed on the wrong node and Execution vanished (figma showed 4).
  return defs.map((d, i) => ({
    ...d,
    name: engine === 'sabre' ? SABRE_STAGE_NAMES[i] : d.name,
    model: engine === 'sabre' ? (i === 2 ? 'Sabre reasoner' : 'deterministic') : d.model,
    status: i < done ? 'done'
      : (i === done && status === 'running') ? 'running'
      : (i === done && status === 'failed') ? 'failed'
      : (i === done && (status === 'needs_review' || status === 'review')) ? 'review'
      : 'queued',
    tokens: i < done ? '—' : '—',
    elapsed: i < done ? '—' : '—',
  }));
}

export function mapBuild(b) {
  const client = ensureClient(b.client_slug);
  return {
    id: b.job_id || b.id,
    name: b.name || b.job_id || 'build',
    client,
    source: b.source || 'pdf',
    engine: b.engine || null,   // PY-10 P3: drives the engine-aware UI (codex Process card / engine tag)
    status: b.status || 'queued',
    verdict: b.verdict || null,
    // Build credit (Viper handoff): /builds joins builds.owner_id → builder and
    // meta.review.by_user_id → approver. builderId/approvedById are ext_ids that
    // resolve via MAV.memberById; `owner` keeps the real builder NAME as the
    // legacy fallback (was hardcoded 'You').
    builderId: b.builder_ext || null,
    approvedById: b.approver_ext || null,
    owner: b.builder_name || 'You',
    started: fmtAgo(b.started_at || b.created_at),
    tokensK: b.tokens_k || 0,
    cost: Number(b.cost) || 0,
    confidence: (b.confidence == null) ? null : Number(b.confidence),
    activeStage: b.active_stage || 0,
    stages: synthStages(b.status, b.source, b.active_stage, b.engine),
    _id: b.id,      // real DB uuid — kept for the detail fetch (jobdetail)
    _live: true,
  };
}

function patchClientBySlug() {
  const M = window.MAV;
  if (M.__cbsPatched) return;
  const orig = M.clientBySlug;
  M.clientBySlug = (s) => orig(s) || synthClient(s || 'unknown');
  M.__cbsPatched = true;
}

// Map a live /api/data/clients row → the shape MAV.clients was mocked as in
// frontend/src/screens/data.js (so the KB grid + the dashboard's ClientBadge
// drop in without rewriting screens). Attrs JSONB carries the array-valued
// fields (colors/flags/esps/presets/initials) the server doesn't have columns
// for; missing attrs default to safe empties so older rows render.
function mapClient(c) {
  const attrs = c.attrs || {};
  const initials = attrs.initials || (c.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '??';
  return {
    slug: c.slug,
    name: c.name,
    color: c.color || '#3B3C43',
    accent: c.accent || c.color || '#3B3C43',
    initials,
    colors: Array.isArray(attrs.colors) ? attrs.colors : [],
    font: c.font || 'Arial',
    width: c.width || 600,
    refs: c.refs || 0,
    flags: Array.isArray(attrs.flags) ? attrs.flags : [],
    esp: c.esp || null,
    esps: Array.isArray(attrs.esps) ? attrs.esps : (c.esp ? [c.esp] : []),
    presets: Array.isArray(attrs.presets) ? attrs.presets : [],
  };
}

// Build a KB profile stub from a lite client row — everything EXCEPT the
// approved_markdown (which the lite /clients endpoint deliberately omits to
// keep login fast). The full markdown is fetched lazily by KBProfile when the
// user opens that client's page; see window.GooseLoadKbProfile below.
function mapKbProfileStub(c) {
  return {
    slug: c.slug,
    name: c.name,
    sourcePath: c.source_path || `email-kb/02-clients/${c.slug}.md`,
    approvedMarkdown: '',   // hydrated on-demand
    versionId: c.approved_version_id || 'v1.0',
    approvedBy: c.approved_by_name || 'KB import',
    approvedAt: c.approved_at ? new Date(c.approved_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—',
    _stub: true,
  };
}

// Map a live /api/data/team user row → the shape MAV.team was mocked as.
// Fields the schema lacks (rating/comment/recent/key/timeSaved/wouldShip)
// default to honest neutral values so admin.jsx renders without a NaN/crash
// but doesn't fake data. `pod` is the pod ext_id (the slug the rest of the
// admin UI uses), resolved from pod_id via the podsByUuid lookup.
function mapTeamMember(u, podsByUuid) {
  const pod = u.pod_id && podsByUuid[u.pod_id] ? podsByUuid[u.pod_id].ext_id : null;
  // Display role: the mock used long strings like "Admin · Producer"; the
  // schema enum is short. Map back to friendly forms; unknown roles pass through.
  const roleDisplay = { admin: 'Admin · Workspace manager', team_lead: 'Team lead · Senior', dev: 'Dev · Builder', sme: 'SME · Reviewer', product_manager: 'Product Manager' }[u.role] || u.role || 'Dev';
  const used = Number(u.cost_sum || 0);
  const lim = u.spend_limit == null ? 0 : Number(u.spend_limit);
  return {
    id: u.ext_id || u.email || u.id,    // ext_id stays the human-readable slug ('jd', 'ar')
    name: u.name || u.email || '—',
    initials: u.initials || (u.name || '?').slice(0, 2).toUpperCase(),
    role: roleDisplay,
    pod,
    color: u.color || '#6334E3',
    status: u.status || 'offline',
    // usage aggregates (0 today; project owner_id and these light up)
    builds: Number(u.builds_count || 0),
    tokensK: Number(u.tokens_k_sum || 0),
    spend: used,
    used,
    limit: lim,
    key: '----',                        // schema doesn't store key suffixes
    rating: 0,                          // schema doesn't carry ratings yet
    afterburnersLeft: Number(u.afterburners_left || 0),
    afterburnersCap: Number(u.afterburners_cap || 0),
    comment: '',                        // mock-only; would need a ratings table
    lastBuild: '',                      // mock-only; would join newest build by owner_id
    recent: [],                         // mock-only; would join recent builds by owner_id
    timeSaved: 0,                       // mock-only; survey/instrumentation field
    wouldShip: 'ship',                  // mock-only; defaults to neutral
    _id: u.id,
    _live: true,
  };
}

// Map a live /api/data/team pod row → the shape MAV.pods was mocked as.
// dept/subDept become the org_unit ext_ids ('email' / 'dedicated' / 'retail')
// matching the prototype's data.js so admin.jsx's department tree renders.
function mapTeamPod(p) {
  return {
    id: p.ext_id || p.id,
    name: p.name,
    dept: p.dept_ext || null,
    subDept: p.sub_dept_ext || null,
    leadId: null,                       // resolved by lead_user_id (uuid) → ext_id below
    lead: p.lead_initials || (p.lead_name || '—'),
    clientSlugs: [],                    // no schema link yet (out of scope, item-4 follow-up)
    clients: '',
    _leadUuid: p.lead_user_id || null,
    _id: p.id,
    _live: true,
  };
}

// Map a live /api/issues row → the MAV.issues mock shape. The server already
// emits the mock field names (id/type/sev/status/state/title/text/build/thread/
// labels/watchers/assignee/reporter) + a per-row `when`; we just backfill `when`
// from `created` when the server didn't supply it (reuse fmtAgo). Pass-through
// otherwise.
function mapIssue(i) {
  return { ...i, when: i.when || fmtAgo(i.created) };
}

// Lazy markdown fetch for one client. Returns the populated profile or null on
// failure (caller falls back to the mock/stub). Cached on MAV.kbProfiles so a
// second open is a no-op.
async function loadKbProfile(slug) {
  if (!slug || !window.GooseAPI || !window.GooseAPI.client) return null;
  const M = window.MAV;
  const existing = M.kbProfiles && M.kbProfiles[slug];
  if (existing && !existing._stub && existing.approvedMarkdown) return existing;
  try {
    const r = await window.GooseAPI.client(slug);
    const c = r && r.client;
    if (!c || !c.approved_markdown) return existing || null;
    const full = { ...mapKbProfileStub(c), approvedMarkdown: c.approved_markdown };
    delete full._stub;
    M.kbProfiles = M.kbProfiles || {};
    M.kbProfiles[slug] = full;
    return full;
  } catch (_) { return existing || null; }
}
if (typeof window !== 'undefined') window.GooseLoadKbProfile = loadKbProfile;

// Derive 2-letter initials from a display name. "Aum Shukla" → "AS",
// "P. Shah" → "PS", "TJ" → "TJ", "" → "??". Used by the sidebar + topbar
// avatars (and anywhere else that needs initials for a live user).
export function initialsFrom(name) {
  const raw = String(name || '').trim();
  if (!raw) return '??';
  // Drop punctuation, keep alphanumeric word chars
  const words = raw.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  if (!words.length) return raw.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  // First-letter-of-first + first-letter-of-last (handles middle names cleanly)
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
if (typeof window !== 'undefined') window.GooseInitialsFrom = initialsFrom;

// Fetch live builds and replace MAV.jobs. Returns {ok, count|reason}.
// Also fetches /summary (dashboard stat tiles) and /clients (KB grid + KB
// profile view) — each independent of the builds fetch; any may fail without
// taking the others down, and any may silently fall back to mock on error.
async function hydrateDemo() {
  patchClientBySlug();    // make the UI crash-proof regardless of what loads
  // summary + clients are best-effort and independent — kick them off in
  // parallel so the builds wait covers them both for free.
  // Resolve the signed-in user once per hydrate so the topbar/sidebar avatars
  // can pull their initials/name/role from the live row instead of the mock
  // 'jd' lookup. /me already runs in app.jsx; calling again is cheap and gives
  // us a single source of truth on MAV.currentUser.
  const meP = GooseAPI.me()
    .then((r) => {
      if (r && r.user) {
        const u = r.user;
        window.MAV.currentUser = {
          id: u.id,
          ext_id: u.ext_id,   // (not on /me — undefined today; sourced elsewhere if ever needed)
          email: u.email,
          name: u.name,
          role: u.role,
          initials: initialsFrom(u.name),
          workspaceId: u.workspace_id,   // RBAC-07 F2 fix: /me's `user` row is snake_case
        };
        // RBAC-07: carry the membership/tier fields + derive the ONE viewer source
        // (window.MAV.viewer) that the nav/switcher/action-gates read.
        window.MAV.memberships = r.memberships || [];
        window.MAV.orgTier = r.orgTier || null;
        window.MAV.activeWorkspaceId = r.activeWorkspaceId || null;
        // ORG-02: /me now carries the caller's active client assignments — the
        // newbuild picker scopes sme/dev to these (server enforces at /start regardless).
        window.MAV.me = { assignments: r.assignments || [] };
        if (window.RBAC) { window.RBAC.setViewer(window.RBAC.viewerFromMe(r)); window.MAV.viewer = window.RBAC.viewer(); }
      }
    })
    .catch(() => {});
  const summaryP = GooseAPI.summary()
    .then((r) => { if (r && r.summary) window.MAV.summary = r.summary; })
    .catch(() => {});
  // Cost-by-stage for the dashboard's "Cost by stage" panel — workspace-wide
  // per-stage roll-up. Best-effort like summary; dashboard falls back to mock
  // when MAV.costByStage isn't populated.
  const costByStageP = GooseAPI.costByStage()
    .then((r) => {
      if (r && Array.isArray(r.costByStage)) {
        window.MAV.costByStage = { rows: r.costByStage, totalCost: Number(r.totalCost) || 0 };
      }
    })
    .catch(() => {});
  // Codex usage (VD-01): kick the sidecar fetch HERE — post-auth, GooseAPI ready. data.js's
  // module-load refreshCodexUsage() no-ops because api.js loads AFTER data.js (GooseAPI is
  // undefined at that point) and it is never retried, so without this the codex tiles stay
  // 'usage unavailable' forever even when the sidecar has data.
  if (window.MAV && window.MAV.refreshCodexUsage) window.MAV.refreshCodexUsage();
  // Metric drill-down aggregates (verdict/source/client/daily/avgElapsedByStage)
  // for the four screens reached via the dashboard stat tiles. Best-effort.
  const metricsP = GooseAPI.metrics()
    .then((r) => { if (r) window.MAV.metrics = r; })
    .catch(() => {});
  const clientsP = GooseAPI.clients()
    .then((r) => {
      const live = (r && r.clients) || [];
      if (!live.length) return;
      const M = window.MAV;
      M.clients = live.map(mapClient);
      // Seed kbProfiles with stubs (no markdown) so the KB grid + profile chrome
      // can render immediately. The full markdown is fetched on profile-open
      // via window.GooseLoadKbProfile (see loadKbProfile above) — keeps the
      // login bundle small (was 160KB → ~10KB).
      M.kbProfiles = M.kbProfiles || {};
      live.forEach((c) => { M.kbProfiles[c.slug] = mapKbProfileStub(c); });
      M.__liveClients = true;
    })
    .catch(() => {});
  const teamP = GooseAPI.team()
    .then((r) => {
      const liveTeam = (r && r.team) || [];
      const livePods = (r && r.pods) || [];
      if (!liveTeam.length && !livePods.length) return;
      const M = window.MAV;
      // Index pods by uuid so mapTeamMember can resolve pod_id → pod ext_id slug
      const podsByUuid = Object.fromEntries(livePods.map((p) => [p.id, p]));
      if (livePods.length) M.pods = livePods.map(mapTeamPod);
      if (liveTeam.length) {
        M.team = liveTeam.map((u) => mapTeamMember(u, podsByUuid));
        // memberById was a closure over the MOCK roster (data.js:261) and was
        // never re-pointed when M.team went live — so any live-only ext_id
        // (as/dc/ps/ri/tb; e.g. Aum Shukla 'as', the signed-in user) resolved to
        // undefined, blanking comment authors / assignees / watchers / reporters
        // across issueticket/admin/issuesdir/kbadmin/profile. Re-point it at the
        // live team here. The offline/catch path leaves M.team AND the mock
        // memberById untouched, so jd/ar/ml/ko still resolve when the fetch fails.
        M.memberById = (id) => M.team.find((m) => m.id === id) || null;
        // Second pass: resolve pod.leadId from the user's ext_id (the slug the
        // admin UI uses to display the lead by id) once both arrays are mapped.
        const teamByUuid = Object.fromEntries(M.team.map((m) => [m._id, m.id]));
        M.pods.forEach((p) => { if (p._leadUuid) p.leadId = teamByUuid[p._leadUuid] || null; });
      }
      M.__liveTeam = true;
    })
    .catch(() => {});
  // Issues (Jira-lite directory). Best-effort + empty→keep-mock guard (so the
  // demo never shows an empty directory before any real issue exists).
  const issuesP = GooseAPI.issues({ pageSize: 200 })
    .then((r) => {
      if (r && Array.isArray(r.rows) && r.rows.length) {
        window.MAV.issues = r.rows.map(mapIssue);
        window.MAV.__liveIssues = true;
      }
    })
    .catch(() => {});
  // Ratings summary overlays per-builder rating/comment/timeSaved/wouldShip onto
  // the team rows + replaces stageScores/passRate. MUST land after teamP so the
  // overlay hits the mapped MAV.team (not the mock array) → chain, don't parallelize.
  const ratingsP = teamP.then(() => GooseAPI.ratingsSummary())
    .then((r) => {
      if (!r) return;
      (r.bySubject || []).forEach((s) => {
        const m = (window.MAV.team || []).find((x) => x.id === s.ext_id);
        if (!m) return;
        if (s.rating != null) m.rating = s.rating;
        if (s.comment) m.comment = s.comment;
        if (s.timeSaved != null) m.timeSaved = s.timeSaved;
        if (s.wouldShip) m.wouldShip = s.wouldShip;
        if (s.lastBuild) m.lastBuild = s.lastBuild;
      });
      if (Array.isArray(r.stageScores) && r.stageScores.length) {
        const names = { ingest: 'Ingestion', semantic: 'Semantic', plan: 'Planning', exec: 'Execution', qa: 'QA', approve: 'Approval' };
        window.MAV.stageScores = r.stageScores.map((s) => ({ id: s.id, name: names[s.id] || s.id, helped: s.helped, hurt: s.hurt }));
      }
      if (r.passRate != null) window.MAV.passRate = r.passRate;
    })
    .catch(() => {});
  // KB edit proposals (the approval queue + each dev's pending-submission state).
  // Lite list (no markdown bodies); the diff view lazy-fetches the full record by
  // key. Best-effort: on failure (e.g. pre-migration) we keep the mock kbRequests.
  const kbProposalsP = GooseAPI.kbProposals()
    .then((r) => {
      if (r && Array.isArray(r.proposals)) {
        window.MAV.kbRequests = r.proposals;
        window.MAV.__liveKbRequests = true;
      }
    })
    .catch(() => {});
  try {
    const res = await GooseAPI.builds();
    const live = (res && res.builds) || [];
    await Promise.all([meP, summaryP, clientsP, teamP, costByStageP, metricsP, issuesP, ratingsP, kbProposalsP]);
    if (!live.length) return { ok: false, reason: 'empty' };
    // Merge, don't clobber: preserve any client-side stub (a just-started build
    // NewBuild prepended to MAV.jobs) whose job_id hasn't appeared in the live DB
    // set yet — the projector takes ~15s to write the real row, and without this
    // the fresh build vanishes from every list (no route back) on the next
    // hydrate. Each stub is dropped automatically once its real row lands (same
    // job_id → same `id`, since mapBuild sets id = b.job_id).
    const liveJobs = live.map(mapBuild);
    const liveIds = new Set(liveJobs.map((j) => j.id));
    const survivingStubs = (Array.isArray(window.MAV.jobs) ? window.MAV.jobs : [])
      .filter((j) => j && j._stub && !liveIds.has(j.id));
    window.MAV.jobs = [...survivingStubs, ...liveJobs];
    window.MAV.__live = true;
    return { ok: true, count: window.MAV.jobs.length };
  } catch (e) {
    await Promise.all([meP, summaryP, clientsP, teamP, costByStageP, metricsP, issuesP, ratingsP, kbProposalsP]);
    return { ok: false, reason: (e && e.kind) || 'error' };
  }
}

// ── RBAC-07 GAP-4: context-aware build loading (THE cross-tenant routing) ────────
// Org breadth comes ONLY from the audited /api/org/builds — NEVER a relaxed tenant
// query. A lead's "all my teams" is a CLIENT-side per-workspace fan-out over
// single-tenant calls; it must never touch /api/org/builds (403 at layer 1 anyway).
// org rows are thinner + snake_case (F4): build_id, no name/status/client_slug.
function mapOrgRow(b) {
  return {
    id: b.build_id, _id: b.build_id, job_id: b.job_id,
    name: b.job_id, client: null,            // org rows carry no client_slug
    status: b.verdict === 'PASS' ? 'complete' : b.verdict === 'FAIL' ? 'failed' : (b.verdict ? 'needs_review' : 'running'),
    verdict: b.verdict || null,
    cost: b.cost, confidence: b.confidence, started: b.created_at,
    _wsSlug: b.workspace_slug, _org: true,
  };
}

// Fetch builds for the viewer's active context and replace MAV.jobs, then re-render.
// Single ws → the normal tenant hydrate; 'org' → orgBuilds(); 'all' → lead fan-out.
async function loadDemoContextBuilds(viewer) {
  const v = viewer || (window.RBAC && window.RBAC.viewer());
  if (!v) return { ok: false, reason: 'no-viewer' };
  const api = window.GooseAPI;
  // The routing decision IS rbac.buildsPlan (A5 leak guard) — the one source.
  const plan = (window.RBAC && window.RBAC.buildsPlan) ? window.RBAC.buildsPlan(v)
    : { source: v.ctx === 'org' ? 'org' : (v.ctx === 'all' ? 'fanout' : 'single') };
  try {
    if (plan.source === 'org') {
      const r = await api.orgBuilds();                                  // audited cross-tenant path ONLY
      window.MAV.jobs = (r && r.builds ? r.builds : []).map(mapOrgRow);
      window.MAV.viewerContext = 'org';
    } else if (plan.source === 'fanout') {
      const wss = (v.led && v.led.length ? v.led : v.memberships) || [];  // single-tenant fan-out + union
      const out = [];
      for (const m of wss) {
        try {
          await api.switchWorkspace(m.workspaceId);
          const r = await api.builds();
          (r && r.builds ? r.builds : []).map(mapBuild).forEach((j) => out.push({ ...j, _wsSlug: m.slug, _wsName: m.name }));
        } catch (_) { /* skip a workspace that errors; keep the rest of the union */ }
      }
      if (v.activeWorkspaceId) { try { await api.switchWorkspace(v.activeWorkspaceId); } catch (_) {} }  // restore session
      window.MAV.jobs = out;
      window.MAV.viewerContext = 'all';
    } else {
      await hydrateDemo();                                               // explicit demo only
      window.MAV.viewerContext = v.ctx;
    }
  } catch (e) {
    window.MAV.viewerContext = v.ctx;                                   // leave prior jobs; surface nothing cross-tenant
  }
  try { if (window.MAV) window.MAV.viewer = (window.RBAC && window.RBAC.viewer()) || window.MAV.viewer; window.dispatchEvent(new CustomEvent('goose-rbac-change')); } catch (_) {}
  return { ok: true, context: window.MAV.viewerContext, count: (window.MAV.jobs || []).length };
}

const demo = import.meta.env?.VITE_MOCK_AUTH === '1';
const live = typeof window === 'undefined' ? null : createLiveHydrator(GooseAPI, {
  synthClient, mapBuild, mapClient, mapKbProfileStub, mapTeamPod, mapTeamMember, mapIssue, mapOrgRow, initialsFrom,
});
export const hydrate = (...args) => demo ? hydrateDemo(...args) : live.hydrate(...args);
export const loadContextBuilds = (...args) => demo ? loadDemoContextBuilds(...args) : live.hydrate();
if (typeof window !== 'undefined') {
  window.GooseHydrate = hydrate; window.GooseLoadContext = loadContextBuilds;
  window.GooseAuthenticate = (...args) => live.authenticate(...args);
  window.GooseSwitchContext = (...args) => live.switchContext(...args);
  window.GooseSignOut = (...args) => live.signOut(...args);
  if (!demo) { window.GooseLoadKbProfile = live.loadKbProfile; live.reset(); }
}
export default hydrate;
